import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faRotateRight, faWandMagicSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { TextArea } from "@/components/TextArea/TextArea";
import { AiModelUseCase, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import {
	SceneArtifactKind,
	SceneArtifactState,
	VideoWizard,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { SceneCard } from "../components/SceneCard";
import { ModelSelectRow } from "../ModelSelectRow";
import { isStepApproved } from "../wizard.utils";

interface Step5SceneVideosProps {
	wizard: VideoWizard;
	isBusy: boolean;
	onSelectModel: (aiModelId: number) => void;
	onGenerate: (sceneIds?: number[], feedback?: string) => void;
	onApproveScene: (sceneId: number) => void;
	onApprove: () => void;
}

/**
 * Step 4 — one video per scene, generated from that scene's own example image.
 *
 * The most expensive step in the product by an order of magnitude, and the only one whose
 * results are approved one at a time. A finished render is *"Prüfung offen"* and nothing
 * more: the machine has produced a file, which is not the same claim as a person having
 * watched it. Step 5 opens only once every scene has been through that separately.
 *
 * Every figure on every button is the server's, including the per-scene one — a per-second
 * price cannot be turned into a per-scene price without knowing what the video model
 * rounds a scene's length up to.
 */
export const Step5SceneVideos: FunctionComponent<Step5SceneVideosProps> = ({
	wizard,
	isBusy,
	onSelectModel,
	onGenerate,
	onApproveScene,
	onApprove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [feedback, setFeedback] = useState("");

	const { scenes } = wizard.storyboard;
	const approved = isStepApproved(wizard, 5);

	// A scene is "rendering" while it has a job in flight — the aggregate's answer, not ours.
	const jobsByScene = useMemo(() => {
		const map = new Map<number, number>();

		for (const job of wizard.activeJobs) {
			if (job.type === MediaJobType.SCENE_VIDEO && job.storyboardSceneId) {
				map.set(job.storyboardSceneId, job.progress);
			}
		}

		return map;
	}, [wizard.activeJobs]);

	const hasVideos = scenes.some((scene) => !!scene.video);
	const renderingCount = scenes.filter(
		(scene) => jobsByScene.has(scene.id) || scene.video?.state === SceneArtifactState.GENERATING
	).length;
	const approvedCount = scenes.filter(
		(scene) => scene.video?.state === SceneArtifactState.APPROVED
	).length;
	// The server refuses the step unless every scene is approved individually; saying so on
	// the button beats a request that comes back 422.
	const allApproved = !!scenes.length && approvedCount === scenes.length;

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("steps.sceneVideos.heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">
						{ts("steps.sceneVideos.description")}
					</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: hasVideos
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("steps.sceneVideos.approvedBadge")
						: renderingCount
							? ts("steps.sceneVideos.renderingBadge", { count: renderingCount })
							: hasVideos
								? ts("steps.sceneVideos.reviewBadge")
								: ts("status.notStarted")}
				</span>
			</header>

			<ModelSelectRow
				disabled={isBusy || !!renderingCount}
				step={5}
				useCase={AiModelUseCase.SCENE_VIDEO}
				wizard={wizard}
				onSelectModel={onSelectModel}
			/>

			{!hasVideos && !renderingCount ? (
				<div className="rounded-card border border-dashed border-border bg-surface px-4 py-8 text-center">
					<p className="mx-auto max-w-[560px] text-sm text-subtle">
						{ts("steps.sceneVideos.empty")}
					</p>
					<div className="mt-4 flex justify-center">
						<Button
							color={ButtonColor.ACTION}
							disabled={isBusy || !scenes.length}
							image={<Icon className="mr-2" icon={faWandMagicSparkles} />}
							size={ButtonSize.M}
							title={ts("steps.sceneVideos.generateAll", {
								scenes: scenes.length,
							})}
							type="button"
							onClick={() => onGenerate()}
						/>
					</div>
					<p className="mt-2 text-xs text-subtle">{ts("steps.sceneVideos.asyncHint")}</p>
				</div>
			) : (
				<>
					<p className="text-sm font-bold text-ink">{ts("steps.sceneVideos.resultHeading")}</p>
					{/* These clips carry the video model's own ambience and no speech — the
					    narration is mixed in per scene at export, re-timed against the trim.
					    Said here rather than only in the empty state above, which is gone by
					    the time there is anything to watch. */}
					<p className="text-xs text-subtle">{ts("steps.sceneVideos.noVoiceHint")}</p>

					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{scenes.map((scene, index) => (
							<SceneCard
								key={scene.id}
								approveLabel={ts("steps.sceneVideos.approveScene")}
								artifact={scene.video}
								disabled={isBusy}
								i18nScope="steps.sceneVideos"
								index={index}
								isGenerating={jobsByScene.has(scene.id)}
								kind={SceneArtifactKind.VIDEO}
								progress={jobsByScene.get(scene.id)}
								regenerateLabel={ts("steps.sceneVideos.regenerateScene")}
								scene={scene}
								onApprove={() => onApproveScene(scene.id)}
								onRegenerate={(sceneFeedback) => onGenerate([scene.id], sceneFeedback)}
							/>
						))}
					</div>

					<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<span className="text-sm font-bold text-ink">
								{ts("steps.sceneVideos.iterateHeading")}
							</span>
							<span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
								{renderingCount
									? ts("steps.sceneVideos.progressRendering", {
											approved: approvedCount,
											scenes: scenes.length,
											times: renderingCount,
										})
									: ts("steps.sceneVideos.progress", {
											approved: approvedCount,
											scenes: scenes.length,
										})}
							</span>
						</div>

						<TextArea
							showError
							handleChange={(event) => setFeedback(event.target.value)}
							name="wizard-video-feedback"
							placeholder={ts("steps.sceneVideos.feedbackPlaceholder")}
							value={feedback}
						/>

						{/* The first frame is the step-2 image, so the look is already decided by the
						    time this box is reachable. Said here rather than only in the placeholder,
						    which is gone the moment the author starts typing. */}
						<p className="text-xs text-subtle">{ts("steps.sceneVideos.feedbackHint")}</p>

						<div className="flex flex-wrap gap-2">
							<Button
								className="flex-1"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isBusy || !!renderingCount || !scenes.length}
								image={<Icon className="mr-2" icon={faRotateRight} />}
								size={ButtonSize.M}
								title={ts("steps.sceneVideos.regenerateAll")}
								type="button"
								onClick={() => {
									onGenerate(undefined, feedback.trim() || undefined);
									// Unsent feedback is deliberately not kept: it has been used.
									setFeedback("");
								}}
							/>
							<Button
								color={ButtonColor.ACTION}
								disabled={isBusy || !allApproved}
								size={ButtonSize.M}
								title={ts("steps.sceneVideos.approve")}
								type="button"
								onClick={onApprove}
							/>
						</div>

						{!allApproved && (
							<p className="text-xs text-subtle">
								{ts("steps.sceneVideos.approveBlockedHint", { scenes: scenes.length })}
							</p>
						)}
					</section>
				</>
			)}
		</div>
	);
};
